// authContext.tsx

import { jwtDecode } from "jwt-decode";
import React, { createContext, useContext, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getAllClientAddress,
  getClientLogo,
  getClientSettings,
} from "src/apis/ClientAPI";
import { getReminderConfig } from "src/apis/reminderConfigApis";
import { ApiResponse } from "../apis";
import { Login } from "../apis/authenticationApis";
import { login, logout } from "../redux/slices/authSlice";
import Feature from "src/common_constants/Feature";
import {
  clearClientSettings,
  setClientSettings,
} from "src/redux/slices/clientSettingsSlice";
import {
  JwtPayload,
  LoginFormData,
  UserData,
} from "../shared_components/interfaces";

// ✅ Helper: fetch public IP in browser (safe fallback = "")
const getPublicIpAddress = async (): Promise<string> => {
  try {
    const res = await fetch("https://api.ipify.org?format=json", {
      method: "GET",
      cache: "no-store",
    });
    const data = await res.json();
    return data?.ip ?? "";
  } catch (e) {
    console.warn("Failed to fetch public IP:", e);
    return "";
  }
};

// Define the shape of the authentication context
interface AuthContextType {
  isAuthenticated: boolean | null;
  token: string | null;
  currentUser: any | null;
  signIn: (data: LoginFormData) => Promise<boolean | undefined>;
  logo: any;
  country: string;
  fetchLogo: (id: string) => void;
  fetchClientCountry: (id: number) => void;
  signOut: () => void;
  isValidToken: (token: string) => boolean;
}

interface AuthProviderProps {
  children?: React.ReactNode;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(false);
  const [token, setToken] = useState<string | null>(null);
  const [logo, setLogo] = useState<any>(null);
  const [country, setCountry] = useState<string>("");

  const [currentUser, setCurrentUser] = useState<any | null>({
    userId: null,
    clientId: null,
  });

  const dispatch = useDispatch();

  //fetch Logo
  const fetchLogo = async (clientId: string) => {
    try {
      const res = await getClientLogo(clientId);
      if (res.status === 200) {
        setLogo(res.data[0]);
      }
    } catch (error) {
      console.error("Error fetching client logo:", error);
    }
  };

  const fetchClientCountry = async (clientId: number) => {
    try {
      const res = await getAllClientAddress(clientId);
      if (res.status === 200) {
        setCountry(res?.data[0]?.countryId);
      }
    } catch (error) {
      console.error("Error fetching client country:", error);
    }
  };

  // Login function
  const signIn = async (data: LoginFormData) => {
    try {
      const response: ApiResponse = await Login(data);

      const token = response.data.token;
      const decoded = jwtDecode(token) as JwtPayload;

      const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;

      setToken(token);
      tokenKey && (await localStorage.setItem(tokenKey, token));

      const ipAddress = await getPublicIpAddress();

      const userData: UserData = {
        clientId: decoded.ClientId,
        userId: decoded.UserId,
        userName: decoded.UserName,
        firstName: decoded.firstName,
        lastName: decoded.lastName,
        exp: decoded.exp,
        permissions: [],
        cultureId: decoded.CultureId,
        token: token,
        ipAddress, // ✅ correct key
      };

      setIsAuthenticated(true);

      setCurrentUser({
        userId: decoded.UserId,
        clientId: decoded.ClientId,
        username: decoded.UserName,
        firsName: decoded.firstName,
        lastName: decoded.lastName,
      });

      // Optional: load logo/country if needed
      // decoded.ClientId && (await fetchLogo(decoded.ClientId));
      // decoded.ClientId && (await fetchClientCountry(Number(decoded.ClientId)));

      // ✅ Store in redux
      await dispatch(login(userData));

      // ✅ Client settings
      const res: ApiResponse = await getClientSettings();
      const resConfig: ApiResponse = await getReminderConfig();

      if (res.data) {
        dispatch(
          setClientSettings({
            ...res.data,
            needToApply: resConfig?.data?.needToApply ?? false,
          }),
        );
      }

      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // Logout function
  const signOut = () => {
    setIsAuthenticated(false);
    setToken(null);

    const tokenKey = `${process.env.REACT_APP_JWT_TOKEN_KEY}`;
    tokenKey && localStorage.removeItem(tokenKey);

    // Clear custom validation rules cache
    localStorage.removeItem(`feature_${Feature.CUSTOM_VALIDATION_RULES}`);

    dispatch(clearClientSettings());
    dispatch(logout());
  };

  const isValidToken = (token: string) => {
    const decodedToken: JwtPayload = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return (decodedToken.exp && decodedToken.exp > currentTime) || false;
  };

  // Provide the context value to its children
  return (
    <AuthContext.Provider
      value={{
        token,
        logo,
        fetchLogo,
        country,
        fetchClientCountry,
        signIn,
        signOut,
        currentUser,
        isAuthenticated,
        isValidToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
